import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import { initials } from "./shared";

export const NewChatDialog = ({ open, onClose, onCreated }) => {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/internal-chats/users");
        setUsers(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
    setSelected([]);
    setTitle("");
    setSearch("");
    setTab(0);
  }, [open]);

  const isGroup = tab === 1;

  const toggle = userId => {
    if (!isGroup) {
      setSelected([userId]);
      return;
    }
    setSelected(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSave = async () => {
    if (!selected.length) {
      toast.warn("Selecione pelo menos um atendente.");
      return;
    }
    if (isGroup && !title.trim()) {
      toast.warn("Dê um nome ao grupo.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/internal-chats", {
        userIds: selected,
        title: isGroup ? title.trim() : null,
        isGroup
      });
      onCreated(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Nova conversa interna</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(e, value) => {
            setTab(value);
            setSelected([]);
          }}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label="Direta" />
          <Tab label="Grupo" />
        </Tabs>

        {isGroup && (
          <TextField
            label="Nome do grupo"
            fullWidth
            size="small"
            value={title}
            onChange={e => setTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          placeholder="Buscar atendente"
          fullWidth
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 1 }}
        />

        <List dense sx={{ maxHeight: 300, overflowY: "auto" }}>
          {filtered.map(user => (
            <ListItemButton
              key={user.id}
              onClick={() => toggle(user.id)}
              selected={selected.includes(user.id)}
            >
              <ListItemAvatar>
                <Badge
                  color="success"
                  variant="dot"
                  overlap="circular"
                  invisible={!user.online}
                >
                  <Avatar sx={{ width: 34, height: 34, fontSize: 14 }}>
                    {initials(user.name)}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={user.name}
                secondary={user.online ? "Online" : "Offline"}
              />
              {isGroup && <Checkbox checked={selected.includes(user.id)} />}
            </ListItemButton>
          ))}
          {!filtered.length && (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
              Nenhum atendente encontrado.
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : "Criar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AddParticipantsDialog = ({ open, onClose, chat, onUpdated }) => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !chat) return;
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/internal-chats/users");
        const current = new Set((chat.participants || []).map(p => p.userId));
        setUsers(data.filter(u => !current.has(u.id)));
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
    setSelected([]);
  }, [open, chat]);

  const handleSave = async () => {
    if (!selected.length) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/internal-chats/${chat.id}/participants`,
        { userIds: selected }
      );
      onUpdated(data);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Adicionar ao grupo</DialogTitle>
      <DialogContent dividers>
        <List dense sx={{ maxHeight: 320, overflowY: "auto" }}>
          {users.map(user => (
            <ListItemButton
              key={user.id}
              onClick={() =>
                setSelected(prev =>
                  prev.includes(user.id)
                    ? prev.filter(id => id !== user.id)
                    : [...prev, user.id]
                )
              }
              selected={selected.includes(user.id)}
            >
              <ListItemAvatar>
                <Avatar sx={{ width: 34, height: 34, fontSize: 14 }}>
                  {initials(user.name)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={user.name} />
              <Checkbox checked={selected.includes(user.id)} />
            </ListItemButton>
          ))}
          {!users.length && (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
              Todos os atendentes já estão no grupo.
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : "Adicionar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
